import { ChevronDown, LoaderCircle } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import {
    AiGeneratedLabel,
    Button,
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
    Input,
    Label,
    MultiSelect,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    SpateAiIcon,
    Switch,
    Text,
} from '@/ui/design-system/components';
import { Textarea } from '@/ui/design-system/components/ui/textarea';
import {
    isChartableListInput,
    isPinnableListInput,
    listViewOf,
    withListView,
} from '@/ui/components/AiAssistant/visuals';
import { useFetchCountries, useFetchTikTokCountries } from 'utils/hooks';
import { useEntity } from 'utils/hooks/api';
import WidgetRowsSource from './WidgetRowsSource';
import WidgetEntitySelect from './WidgetEntitySelect';
import {
    WIDGET_TYPES,
    WIDGET_TYPE_BY_ID,
    matchWidgetType,
    reconcileArgsForType,
} from './widgetCatalog';
import {
    AFFINITY_GROUP_OPTIONS,
    ANALYSIS_LENS_OPTIONS,
    CLUSTER_OPTIONS,
    HEALTH_STAT_FILTERS,
    LIFE_CYCLE_OPTIONS,
    PRESET_OPTIONS,
    PRICE_OPTIONS,
    geoOptions,
    orderByOptionsFor,
} from './widgetOptions';

const ORDER_BY_DEFAULT = '__default__';
const PRESET_NONE = '__none__';

// Any of these gets the type ONE WidgetRowsSource, not one per control.
const ROWS_SOURCE_CONTROLS = ['entity_ids', 'hashtags', 'exclude'];

// Widget configs and the widget envelope name types in the singular; the
// entities API routes on the plural. Anything unrecognized stays unmapped so
// the resolver below never fetches under a guessed type — a wrong-type fetch
// resolves null and poisons the type-free ['entity', …] cache key.
const API_ENTITY_TYPES = { brand: 'brands', category: 'categories' };

// Controls shown directly under the main settings (the common knobs); the rest
// (analysis_lens, affinity_group, branded, granular TikTok filters) live behind
// the "Advanced settings" disclosure so non-power users aren't overwhelmed.
const ADVANCED_CONTROLS = [
    'analysis_lens',
    'affinity_group',
    'branded',
    'filters',
];

// The edit form (modal right pane). Controlled over draftArgs (+ draftTitle, the
// one sibling field): picking a widget type reconciles the args, and each
// control writes its param. No hard validation — the live preview surfaces
// invalid combos.
function WidgetEditForm({
    draftArgs,
    setDraftArgs,
    draftTitle,
    setDraftTitle,
    initialEntityName,
    initialEntityType,
    initialRelatedEntityName,
    describe,
}) {
    const type = matchWidgetType(draftArgs);
    const controls = type?.controls ?? [];
    const dataSource =
        type?.fixed?.data_source ?? draftArgs.data_source ?? 'pop_index';
    const relationship =
        type?.fixed?.entity_relationship ??
        draftArgs.entity_relationship ??
        'self';
    const isTikTok = dataSource === 'tiktok';
    const hasAdvanced = ADVANCED_CONTROLS.some((c) => controls.includes(c));

    const { data: popCountries, isLoading: popCountriesLoading } =
        useFetchCountries();
    const { data: tiktokCountries, isLoading: tiktokCountriesLoading } =
        useFetchTikTokCountries();
    // Only the list this source actually uses gates the picker — the other query
    // is still in flight on first render and would hold it open for nothing.
    const countriesLoading = isTikTok
        ? tiktokCountriesLoading
        : popCountriesLoading;
    const geoOpts = geoOptions(isTikTok ? tiktokCountries : popCountries);

    // The selected entity carries a display name alongside the id; draftArgs only
    // stores id/type/route. Keep them in sync: when the id changes from outside
    // the picker (a type reconcile clearing it, or an NL regen), adopt the new id
    // with an unknown name (the live preview still shows the real entity).
    // The anchor dropped while hashtag pins are active, so the last pin leaving
    // can put it back.
    const anchorStashRef = useRef(null);
    const [entity, setEntity] = useState(() =>
        draftArgs.entity_id
            ? {
                  entity_id: draftArgs.entity_id,
                  // Widget types that don't pin entity_type in their args leave
                  // it undefined; the envelope's backend-resolved type (it
                  // describes exactly this id) fills the gap at seed time only.
                  entity_type: draftArgs.entity_type ?? initialEntityType,
                  name: initialEntityName,
              }
            : null,
    );
    useEffect(() => {
        if (draftArgs.entity_id === entity?.entity_id) return;
        if (!draftArgs.entity_id) {
            setEntity(null);
            return;
        }
        // A restored anchor keeps the name it had; the picker reads
        // `value?.name`, so adopting the id alone would show as unselected.
        const stashed = anchorStashRef.current?.entity;
        setEntity(
            stashed?.entity_id === draftArgs.entity_id
                ? stashed
                : {
                      entity_id: draftArgs.entity_id,
                      entity_type: draftArgs.entity_type,
                      name: undefined,
                  },
        );
    }, [draftArgs.entity_id, entity?.entity_id, draftArgs.entity_type]);

    // Same sync for the pair's related entity — args store only
    // related_entity_id (its type is resolved server-side).
    const [relatedEntity, setRelatedEntity] = useState(() =>
        draftArgs.related_entity_id
            ? {
                  entity_id: draftArgs.related_entity_id,
                  name: initialRelatedEntityName,
              }
            : null,
    );
    useEffect(() => {
        if (draftArgs.related_entity_id !== relatedEntity?.entity_id) {
            setRelatedEntity(
                draftArgs.related_entity_id
                    ? {
                          entity_id: draftArgs.related_entity_id,
                          name: undefined,
                      }
                    : null,
            );
        }
    }, [draftArgs.related_entity_id, relatedEntity?.entity_id]);

    // An id that arrived from outside the picker has no name yet. Resolve it
    // against the widget's own data source — the same id names differently per
    // source — and only when we're actually missing one AND genuinely know the
    // type (never guess: the shared ['entity', …] key is type-free, so a
    // wrong-type miss would cache null for every consumer of this id). The
    // related entity gets no equivalent: its name and type always travel
    // together (envelope pair or picker hit), so a known-type-but-missing-name
    // state is unreachable there.
    const anchorApiType = API_ENTITY_TYPES[entity?.entity_type];
    const { data: resolvedEntity } = useEntity({
        id: entity?.entity_id,
        entityType: anchorApiType,
        dataSource,
        enabled:
            Boolean(entity?.entity_id) &&
            !entity?.name &&
            Boolean(anchorApiType),
    });
    const entityValue = entity
        ? { ...entity, name: entity.name ?? resolvedEntity?.name }
        : null;

    const setArg = (key, value) => {
        // Whoever sets the anchor owns it; a stash from an earlier pin session
        // must not put the old one back.
        if (key === 'entity_id') anchorStashRef.current = null;
        if (key === 'hashtags' && draftArgs.entity_id) {
            anchorStashRef.current = {
                entity_id: draftArgs.entity_id,
                entity_type: draftArgs.entity_type,
                entity_route: draftArgs.entity_route,
                entity,
            };
        }
        setDraftArgs((prev) => {
            const next = { ...prev };
            if (
                value === undefined ||
                value === null ||
                (Array.isArray(value) && value.length === 0)
            ) {
                delete next[key];
            } else {
                next[key] = value;
            }
            // The backend clears the anchor on a pinned hashtag list, but only
            // for that one request — so put it back when the last pin goes,
            // or the widget silently becomes the global list.
            if (key === 'hashtags' && next.hashtags?.length) {
                delete next.entity_id;
                delete next.entity_type;
                delete next.entity_route;
            } else if (key === 'hashtags' && anchorStashRef.current) {
                const stash = anchorStashRef.current;
                next.entity_id = stash.entity_id;
                if (stash.entity_type !== undefined) {
                    next.entity_type = stash.entity_type;
                }
                if (stash.entity_route !== undefined) {
                    next.entity_route = stash.entity_route;
                }
            }
            if (!isChartableListInput(next)) {
                delete next.aggregate;
                delete next.trend_lines;
            }
            return next;
        });
    };

    const setFilter = (key, value) =>
        setDraftArgs((prev) => {
            const filters = { ...(prev.filters || {}) };
            if (!value || (Array.isArray(value) && value.length === 0)) {
                delete filters[key];
            } else {
                filters[key] = value;
            }
            const next = { ...prev };
            if (Object.keys(filters).length === 0) delete next.filters;
            else next.filters = filters;
            return next;
        });

    const handleTypeChange = (typeId) =>
        setDraftArgs((prev) =>
            reconcileArgsForType(prev, WIDGET_TYPE_BY_ID[typeId]),
        );

    const applyEntity = (picked) => {
        setEntity(picked);
        setDraftArgs((prev) => {
            const next = { ...prev };
            if (picked) {
                next.entity_id = picked.entity_id;
                next.entity_type = picked.entity_type;
                next.entity_route = picked.entity_route;
            } else {
                delete next.entity_id;
                delete next.entity_route;
                delete next.entity_type;
            }
            return next;
        });
    };

    const applyRelatedEntity = (picked) => {
        setRelatedEntity(picked);
        setDraftArgs((prev) => {
            const next = { ...prev };
            if (picked) next.related_entity_id = picked.entity_id;
            else delete next.related_entity_id;
            return next;
        });
    };

    const orderByOpts = orderByOptionsFor(dataSource, relationship);

    // --- per-control renderers (shared between the primary section + advanced) ---

    const renderOrderBy = () => (
        <Field key="order_by" id="widget-sort" label="Sort by">
            <Select
                value={draftArgs.order_by ?? ORDER_BY_DEFAULT}
                onValueChange={(v) =>
                    setArg('order_by', v === ORDER_BY_DEFAULT ? undefined : v)
                }
            >
                <SelectTrigger id="widget-sort">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={ORDER_BY_DEFAULT}>Default</SelectItem>
                    {orderByOpts.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </Field>
    );

    const renderPreset = () => (
        <Field key="preset" id="widget-preset" label="Preset">
            <Select
                value={draftArgs.preset ?? PRESET_NONE}
                onValueChange={(v) =>
                    setArg('preset', v === PRESET_NONE ? undefined : v)
                }
            >
                <SelectTrigger id="widget-preset">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={PRESET_NONE}>None</SelectItem>
                    {PRESET_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </Field>
    );

    const renderLifeCycle = () => (
        <MultiSelect
            key="life_cycle"
            label="Life cycle"
            placeholder="Any"
            options={LIFE_CYCLE_OPTIONS}
            value={draftArgs.life_cycle ?? []}
            onChange={(v) => setArg('life_cycle', v)}
        />
    );

    const renderPrice = () => (
        <MultiSelect
            key="price"
            label="Price point"
            placeholder="Any"
            options={PRICE_OPTIONS}
            value={draftArgs.price ?? []}
            onChange={(v) => setArg('price', v)}
        />
    );

    const renderAnalysisLens = () => (
        <Field key="analysis_lens" id="widget-lens" label="Analysis lens">
            <Select
                value={draftArgs.analysis_lens ?? 'overview'}
                onValueChange={(v) => setArg('analysis_lens', v)}
            >
                <SelectTrigger id="widget-lens">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {ANALYSIS_LENS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </Field>
    );

    const renderAffinityGroup = () => (
        <MultiSelect
            key="affinity_group"
            label="Affinity group"
            placeholder="Any"
            options={AFFINITY_GROUP_OPTIONS}
            value={draftArgs.affinity_group ?? []}
            onChange={(v) => setArg('affinity_group', v)}
        />
    );

    const renderBranded = () => (
        <div key="branded" className="flex items-center justify-between">
            <Label
                htmlFor="widget-branded"
                className="text-sm font-medium text-foreground"
            >
                Branded hashtags
            </Label>
            <Switch
                id="widget-branded"
                checked={!!draftArgs.branded}
                onCheckedChange={(c) => setArg('branded', c ? true : undefined)}
            />
        </div>
    );

    const renderFilters = () => (
        <div
            key="filters"
            className="flex flex-col gap-3 rounded-lg border border-border p-3"
        >
            <Text size="sm" className="font-medium">
                Filters
            </Text>
            <MultiSelect
                label="Cluster"
                placeholder="Any"
                options={CLUSTER_OPTIONS}
                value={draftArgs.filters?.cluster ?? []}
                onChange={(v) => setFilter('cluster', v)}
            />
            {HEALTH_STAT_FILTERS.map((dim) => (
                <MultiSelect
                    key={dim.key}
                    label={dim.label}
                    placeholder="Any"
                    options={dim.buckets}
                    value={draftArgs.filters?.[dim.key] ?? []}
                    onChange={(v) => setFilter(dim.key, v)}
                />
            ))}
        </div>
    );

    const renderRowsSource = () => (
        <WidgetRowsSource
            type={type}
            draftArgs={draftArgs}
            setArg={setArg}
            dataSource={dataSource}
            geo={draftArgs.geo}
        />
    );

    const PRIMARY_RENDERERS = {
        order_by: renderOrderBy,
        preset: renderPreset,
        life_cycle: renderLifeCycle,
        price: renderPrice,
    };
    const ADVANCED_RENDERERS = {
        analysis_lens: renderAnalysisLens,
        affinity_group: renderAffinityGroup,
        branded: renderBranded,
        filters: renderFilters,
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Natural language first: describe the widget and it fills out the
                config below. The structured controls double as a menu of what's
                possible. */}
            <DescribeBox describe={describe} />

            {/* Widget type */}
            <Field id="widget-type" label="Widget type">
                <Select value={type?.id ?? ''} onValueChange={handleTypeChange}>
                    <SelectTrigger id="widget-type">
                        <SelectValue placeholder="Choose a widget type" />
                    </SelectTrigger>
                    <SelectContent>
                        {WIDGET_TYPES.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                                {t.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>

            {type && type.entity !== 'none' && !draftArgs.hashtags?.length && (
                <WidgetEntitySelect
                    value={entityValue}
                    entityKind={type.entity}
                    onChange={applyEntity}
                />
            )}

            {/* Related entity (pair-shaped types only). Two brands have no pair
                data, so a brand anchor narrows the search to categories; an
                invalid combo picked the other way round surfaces in the live
                preview (the form does no hard validation). */}
            {type?.relatedEntity && (
                <WidgetEntitySelect
                    id="widget-related-entity"
                    label="Related entity"
                    value={relatedEntity}
                    entityKind={
                        draftArgs.entity_type === 'brand'
                            ? 'category'
                            : 'either'
                    }
                    onChange={applyRelatedEntity}
                />
            )}

            {/* Country */}
            <Field id="widget-geo" label="Country">
                <Select
                    value={draftArgs.geo ?? 'US'}
                    onValueChange={(v) => setArg('geo', v)}
                >
                    <SelectTrigger id="widget-geo">
                        <SelectValue placeholder="Select a country" />
                    </SelectTrigger>
                    <SelectContent>
                        {countriesLoading ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                Loading countries…
                            </div>
                        ) : (
                            geoOpts.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                </SelectItem>
                            ))
                        )}
                    </SelectContent>
                </Select>
            </Field>

            {/* Title — the user's own name for a pinnable list; empty keeps the
                derived title */}
            {isPinnableListInput(draftArgs) && setDraftTitle && (
                <Field id="widget-title" label="Title">
                    <Input
                        id="widget-title"
                        value={draftTitle ?? ''}
                        placeholder="Name this list (optional)"
                        onChange={(e) => setDraftTitle(e.target.value)}
                    />
                </Field>
            )}

            {/* Primary per-type controls (pinned entities / sort / preset /
                life cycle) */}
            {controls.some((c) => ROWS_SOURCE_CONTROLS.includes(c)) &&
                renderRowsSource()}
            {Object.entries(PRIMARY_RENDERERS).map(([key, render]) =>
                controls.includes(key) ? render() : null,
            )}

            {isChartableListInput(draftArgs) && (
                <Field id="widget-render" label="Visualization">
                    <Select
                        value={listViewOf(draftArgs)}
                        onValueChange={(v) =>
                            setDraftArgs((prev) => withListView(prev, v))
                        }
                    >
                        <SelectTrigger id="widget-render">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="table">Table</SelectItem>
                            <SelectItem value="time_series">
                                Time series
                            </SelectItem>
                            <SelectItem value="combined">
                                Combined time series
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
            )}

            {/* Advanced disclosure — the long tail (lens, affinity, branded,
                TikTok health-stat filters) */}
            {hasAdvanced && (
                <Collapsible>
                    <CollapsibleTrigger asChild>
                        <button
                            type="button"
                            className="group flex w-full items-center justify-between rounded-md py-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                        >
                            Advanced settings
                            <ChevronDown
                                size={16}
                                className="transition-transform group-data-[state=open]:rotate-180"
                            />
                        </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="flex flex-col gap-4 pt-3">
                        {Object.entries(ADVANCED_RENDERERS).map(
                            ([key, render]) =>
                                controls.includes(key) ? render() : null,
                        )}
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
}

function Field({ id, label, children }) {
    return (
        <div className="flex flex-col gap-2">
            <Label htmlFor={id} className="text-sm font-medium text-foreground">
                {label}
            </Label>
            {children}
        </div>
    );
}

// Free-text "describe the widget" box — regenerates the whole config via the
// assistant (same harvest path as add-widget).
function DescribeBox({ describe }) {
    const [text, setText] = useState('');
    const disclaimerId = useId();
    const statusId = useId();
    if (!describe) return null;
    const { run, isRunning, error, emptyResult } = describe;
    const status = error
        ? 'Something went wrong — try again.'
        : emptyResult
          ? 'Could not build a widget from that — rephrase?'
          : null;

    const submit = () => {
        if (!text.trim() || isRunning) return;
        run(text);
    };

    return (
        <div className="flex flex-col gap-3 rounded-xl border border-primary/20 bg-accent p-4">
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    <SpateAiIcon size={16} className="text-primary" />
                    <Label
                        htmlFor="widget-describe"
                        className="text-sm font-semibold text-foreground"
                    >
                        Describe this widget
                    </Label>
                </div>
                <Text size="xs" color="muted">
                    Explain what to show in plain text — this replaces the
                    current settings.
                </Text>
            </div>
            <Textarea
                id="widget-describe"
                rows={3}
                placeholder="e.g. top growing skincare brands in the UK"
                aria-describedby={
                    status ? `${disclaimerId} ${statusId}` : disclaimerId
                }
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        submit();
                    }
                }}
                disabled={isRunning}
            />
            {status && (
                <Text id={statusId} size="xs" color="muted">
                    {status}
                </Text>
            )}
            <div className="flex items-center justify-between gap-2">
                {/* No sparkle: the box header already shows one. */}
                <AiGeneratedLabel
                    id={disclaimerId}
                    size="sm"
                    showIcon={false}
                />
                <Button size="sm" onClick={submit} disabled={isRunning}>
                    {isRunning ? (
                        <LoaderCircle size={14} className="animate-spin" />
                    ) : (
                        <SpateAiIcon size={14} />
                    )}
                    Generate
                </Button>
            </div>
        </div>
    );
}

export default WidgetEditForm;
